/**
 * Commission calculation — the money math the whole marketplace rests on.
 *
 * Every commission is derived from a partnership's FROZEN terms snapshot, never
 * from the live Program document. A brand that lowers its rate on Tuesday must
 * not retroactively shrink commissions earned on Monday, and the snapshot is
 * what makes that guarantee mechanical rather than aspirational.
 */

import {
  PLATFORM_FEES_BPS,
  CONVERSION_DRIVEN_TYPES,
  RECURRING_CAPABLE_TYPES,
} from './constants.js';
import { applyBps, formatCents } from './money.js';

/**
 * @typedef {object} Terms
 * @property {'percent_of_sale'|'flat_per_conversion'|'flat_per_post'|'gift_only'} commissionType
 * @property {number} [rateBps]          Required for percent_of_sale. 1500 = 15%.
 * @property {number} [flatAmountCents]  Required for both flat types.
 * @property {number} cookieWindowDays
 * @property {'one_time'|'fixed_period'} [recurringMode] Defaults to one_time.
 * @property {number} [recurringMonths]  Required for fixed_period. Billing cycles paid.
 */

/**
 * @typedef {object} CommissionBreakdown
 * @property {number} grossAmountCents  The commission, by the partnership's terms.
 * @property {number} platformFeeCents  The creator's half of Affily's fee, taken out of the commission.
 * @property {number} brandFeeCents     The brand's half, paid on top of the commission.
 * @property {number} netAmountCents    What actually lands in the creator's payout.
 * @property {number} platformFeeBps    The whole fee rate, recorded for auditability.
 */

/**
 * Gross commission owed for a single conversion (or a single approved post, for
 * flat_per_post). Returns integer cents.
 */
export function calculateGrossCommissionCents(terms, { orderAmountCents = 0 } = {}) {
  assertTerms(terms);

  switch (terms.commissionType) {
    case 'percent_of_sale': {
      if (!Number.isInteger(orderAmountCents) || orderAmountCents < 0) {
        throw new TypeError(
          `percent_of_sale requires a non-negative integer orderAmountCents (got ${orderAmountCents})`,
        );
      }
      return applyBps(orderAmountCents, terms.rateBps);
    }

    case 'flat_per_conversion':
    case 'flat_per_post':
      // The order value is irrelevant to a flat rate — deliberately ignored so a
      // brand reporting $0 orders can't zero out a flat commission.
      return terms.flatAmountCents;

    case 'gift_only':
      // Pays in gifts, never money. Zero rather than a throw so callers that
      // total or project over every program (the calculator, the dashboards)
      // don't each need to special-case a type that simply earns nothing.
      return 0;

    default:
      throw new Error(`Unknown commission type: ${terms.commissionType}`);
  }
}

/**
 * Affily's whole fee on a gross commission, in integer cents. Rounds half-up at
 * the cent — the same rule used everywhere else.
 */
export function calculatePlatformFeeCents(grossAmountCents, feeBps = PLATFORM_FEES_BPS.other) {
  return applyBps(grossAmountCents, feeBps);
}

/**
 * The fee, split down the middle: half paid by the brand on top of the
 * commission, half taken out of what the creator receives. An odd cent stays
 * with the creator, so the halves always add up to the fee exactly.
 */
export function splitFee(feeCents) {
  const creatorFeeCents = Math.floor(feeCents / 2);
  return { creatorFeeCents, brandFeeCents: feeCents - creatorFeeCents };
}

/**
 * Full breakdown for one commission entry. This is the only function that
 * should ever produce the numbers written onto a Commission document.
 *
 * Invariants, always:
 *   grossAmountCents === platformFeeCents + netAmountCents   (the creator's side)
 *   what the brand pays === grossAmountCents + brandFeeCents
 */
export function calculateCommission(terms, { orderAmountCents = 0, feeBps = PLATFORM_FEES_BPS.other } = {}) {
  const grossAmountCents = calculateGrossCommissionCents(terms, { orderAmountCents });
  const { creatorFeeCents, brandFeeCents } = splitFee(calculatePlatformFeeCents(grossAmountCents, feeBps));

  return {
    grossAmountCents,
    platformFeeCents: creatorFeeCents,
    brandFeeCents,
    netAmountCents: grossAmountCents - creatorFeeCents,
    platformFeeBps: feeBps,
  };
}

/**
 * Sum a set of commission breakdowns. Sums the already-rounded per-entry values
 * rather than re-deriving from a total — a payout must equal the sum of the
 * lines a creator can see on their dashboard, to the cent.
 */
export function sumCommissions(commissions) {
  return commissions.reduce(
    (acc, c) => ({
      grossAmountCents: acc.grossAmountCents + (c.grossAmountCents ?? 0),
      platformFeeCents: acc.platformFeeCents + (c.platformFeeCents ?? 0),
      brandFeeCents: acc.brandFeeCents + (c.brandFeeCents ?? 0),
      netAmountCents: acc.netAmountCents + (c.netAmountCents ?? 0),
    }),
    { grossAmountCents: 0, platformFeeCents: 0, brandFeeCents: 0, netAmountCents: 0 },
  );
}

/** True when this commission type accrues from tracked conversions. */
export function isConversionDriven(commissionType) {
  return CONVERSION_DRIVEN_TYPES.includes(commissionType);
}

/** True when these terms pay on renewals rather than only the first sale. */
export function isRecurring(terms) {
  return terms?.recurringMode === 'fixed_period' && (terms.recurringMonths ?? 0) > 0;
}

/** True when this commission type is allowed to recur at all. */
export function isRecurringCapable(commissionType) {
  return RECURRING_CAPABLE_TYPES.includes(commissionType);
}

/**
 * Should this billing cycle be paid?
 *
 * Cycle 1 is the initial sale and always pays — that is the ordinary
 * conversion, whether or not the program recurs. Cycles beyond 1 are renewals
 * and pay only inside the agreed period.
 *
 * The period is counted in BILLING CYCLES, not elapsed months. A brand that
 * bills annually and a brand that bills monthly both mean "the next N invoices"
 * when they say a duration, and cycles are what their webhook actually knows.
 * Elapsed-time arithmetic would also make an annual plan's cycle 2 fall outside
 * a 12-month window, which is the opposite of what everyone intends.
 */
export function isRecurringCyclePayable(terms, billingCycle = 1) {
  if (!Number.isInteger(billingCycle) || billingCycle < 1) {
    throw new TypeError(`billingCycle must be an integer >= 1 (got ${billingCycle})`);
  }
  if (billingCycle === 1) return true;
  if (!isRecurring(terms)) return false;
  return billingCycle <= terms.recurringMonths;
}

/**
 * Why a cycle isn't payable, phrased for the brand's integration logs and the
 * creator's dashboard. Returns null when it is payable.
 */
export function explainUnpayableCycle(terms, billingCycle) {
  if (isRecurringCyclePayable(terms, billingCycle)) return null;
  if (!isRecurring(terms)) {
    return 'This program pays a one-time commission, so renewals do not accrue';
  }
  return `Billing cycle ${billingCycle} is past the ${terms.recurringMonths}-cycle recurring window`;
}

/**
 * Total a creator can expect across the full recurring period, for a steady
 * subscription. Used to make one-time and recurring offers comparable on the
 * public page — the whole reason duration has to be published alongside rate.
 */
export function estimateLifetimeValueCents(terms, { orderAmountCents = 0 } = {}) {
  const perCycle = calculateGrossCommissionCents(terms, { orderAmountCents });
  const cycles = isRecurring(terms) ? terms.recurringMonths : 1;
  return perCycle * cycles;
}

/**
 * Is a conversion inside the partnership's cookie window relative to its click?
 * Boundary rule: exactly-at-the-window counts as inside (<=), matching how
 * every affiliate network states "30 day cookie".
 */
export function isWithinCookieWindow(clickedAt, convertedAt, cookieWindowDays) {
  const elapsedMs = new Date(convertedAt).getTime() - new Date(clickedAt).getTime();
  if (Number.isNaN(elapsedMs)) throw new TypeError('Invalid date passed to isWithinCookieWindow');
  if (elapsedMs < 0) return false; // A conversion can never precede its click.
  return elapsedMs <= cookieWindowDays * 24 * 60 * 60 * 1000;
}

/**
 * Human-readable terms, used identically on the public program page, the
 * partnership row and the commission line. One renderer means the rate a
 * creator reads before applying is literally the same string they see on the
 * commission that gets paid.
 */
export function describeTerms(terms) {
  return [describeRate(terms), describeRecurring(terms)].filter(Boolean).join(', ');
}

/** "A", "A and B", "A, B and 2 more" — names only, in the order given. */
function listGiftNames(gifts) {
  const names = gifts.map((g) => g.name);
  if (names.length <= 2) return names.join(' and ');
  return `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`;
}

/** Gifts a creator can actually be offered. Inactive ones are history, not an offer. */
function activeGifts(gifts) {
  return (gifts ?? []).filter((g) => g && g.isActive !== false);
}

/**
 * The gifts alone, or null when there are none. Stays separate from
 * describeTerms, which is money-only: a commission line on a payout must never
 * mention a gift, and a gift must never be mistaken for a rate.
 *
 * The retail value is the BRAND'S stated figure — it only appears when the
 * brand gave one, and callers that render it label it as brand-stated.
 */
export function describeGifts(gifts, { currency } = {}) {
  const list = activeGifts(gifts);
  if (list.length === 0) return null;
  if (list.length === 1) {
    const [g] = list;
    const value =
      g.retailValueCents == null
        ? ''
        : ` (${formatCents(g.retailValueCents, { dropZeroCents: true, currency })} retail value)`;
    return `Free gift: ${g.name}${value}`;
  }
  return `Free gifts: ${listGiftNames(list)}`;
}

/**
 * The whole offer in one line, for headlines and cards: the money, plus gifts
 * if any. This is what a creator compares across programs, so it never hides
 * the commission behind the gift or the gift behind the commission.
 */
export function describeOffer(terms, gifts) {
  const list = activeGifts(gifts);
  if (terms.commissionType === 'gift_only') {
    return list.length > 0 ? `Gifts only: ${listGiftNames(list)}` : describeRate(terms);
  }
  const money = describeTerms(terms);
  if (list.length === 0) return money;
  return `${money} + free ${list.length === 1 ? 'gift' : 'gifts'}`;
}

/** The rate alone, with no duration. */
export function describeRate(terms) {
  switch (terms.commissionType) {
    case 'percent_of_sale':
      return `${terms.rateBps / 100}% of sale`;
    case 'flat_per_conversion':
      return `${formatCents(terms.flatAmountCents, { currency: terms.currency, dropZeroCents: true })} per conversion`;
    case 'flat_per_post':
      return `${formatCents(terms.flatAmountCents, { currency: terms.currency, dropZeroCents: true })} per post`;
    case 'gift_only':
      return 'Gifts only — no commission';
    default:
      return 'Unknown terms';
  }
}

/**
 * The duration, or null for a one-time commission.
 *
 * This string is not optional decoration. Two SaaS programs both advertising
 * "20% of sale" are offers that differ by an order of magnitude if one recurs
 * for a year — so the duration travels with the rate everywhere the rate is
 * rendered, rather than living in a detail view someone might not open.
 */
export function describeRecurring(terms) {
  if (!isRecurring(terms)) return null;
  const n = terms.recurringMonths;
  return `recurring for ${n} ${n === 1 ? 'billing cycle' : 'billing cycles'}`;
}

function assertTerms(terms) {
  if (!terms || typeof terms !== 'object') {
    throw new TypeError('Commission terms are required');
  }
  if (terms.commissionType === 'gift_only') {
    // Nothing to validate: there is no rate or amount, by definition.
  } else if (terms.commissionType === 'percent_of_sale') {
    if (!Number.isInteger(terms.rateBps) || terms.rateBps < 0) {
      throw new TypeError(
        `percent_of_sale requires a non-negative integer rateBps (got ${terms.rateBps})`,
      );
    }
  } else if (
    terms.commissionType === 'flat_per_conversion' ||
    terms.commissionType === 'flat_per_post'
  ) {
    if (!Number.isInteger(terms.flatAmountCents) || terms.flatAmountCents < 0) {
      throw new TypeError(
        `${terms.commissionType} requires a non-negative integer flatAmountCents (got ${terms.flatAmountCents})`,
      );
    }
  }

  if (terms.recurringMode === 'fixed_period') {
    if (!isRecurringCapable(terms.commissionType)) {
      throw new TypeError(
        `${terms.commissionType} cannot recur — a deliverable is paid once, not per billing cycle`,
      );
    }
    if (!Number.isInteger(terms.recurringMonths) || terms.recurringMonths < 1) {
      throw new TypeError(
        `fixed_period requires an integer recurringMonths >= 1 (got ${terms.recurringMonths})`,
      );
    }
  }
}

// ── Conversion events ───────────────────────────────────────────────────────

/**
 * Per-event payout terms.
 *
 * A program's headline terms describe its PRIMARY event — the action a creator
 * is mainly being asked to drive. Additional events may carry their own terms
 * ($2 a signup alongside 20% of a purchase), or be tracked-only.
 *
 * Tracked-only events matter more than they look. Showing a creator
 * `500 clicks → 40 signups → 3 purchases` tells them whether their audience was
 * unqualified or the brand's checkout is broken; showing only the paying step
 * tells them nothing they can act on.
 */

/**
 * The terms that apply to one event on a set of frozen partnership terms.
 *
 * Falls back to the primary terms when an event is unknown, because a brand
 * firing an event key they never declared is an integration mistake, not a
 * reason to silently pay nothing.
 *
 * @returns {{terms: object|null, event: object|null, payable: boolean, reason: string|null}}
 */
export function resolveEventTerms(frozenTerms, eventKey) {
  if (!frozenTerms) return { terms: null, event: null, payable: false, reason: 'No terms' };

  const events = frozenTerms.events ?? [];
  const key = eventKey || frozenTerms.primaryEventKey || 'purchase';
  const event = events.find((e) => e.key === key) ?? null;

  // Checked before the unknown-key fallback below, which would otherwise credit
  // a stray event against primary terms that have no money in them to credit.
  if (frozenTerms.commissionType === 'gift_only') {
    return {
      terms: null,
      event,
      payable: false,
      reason: 'Gift-only program — tracked, no commission',
    };
  }

  if (!event) {
    // Never against a primary action only the brand records: "SALE" or a typo
    // fired from a tag would otherwise be paid as the sale it cannot report.
    const primary = events.find((e) => e.key === (frozenTerms.primaryEventKey || 'purchase'));
    if (primary?.manualOnly) {
      return {
        terms: null,
        event: null,
        payable: false,
        reason: `Unknown event "${key}" — not credited, because this program's main action is recorded by the brand`,
      };
    }
    // Unknown key. Credit it against the primary terms rather than dropping it,
    // and let the brand see the mismatch in the response.
    return {
      terms: frozenTerms,
      event: null,
      payable: true,
      reason: `Unknown event "${key}" — credited against the primary action`,
    };
  }

  if (!event.isPayable) {
    return {
      terms: null,
      event,
      payable: false,
      reason: `"${key}" is tracked but does not pay a commission on this program`,
    };
  }

  // A payable event carries its own rate; anything it omits falls back to the
  // program's headline terms so a brand need only state what differs.
  return {
    payable: true,
    event,
    reason: null,
    terms: {
      commissionType: event.commissionType ?? frozenTerms.commissionType,
      rateBps: event.rateBps ?? frozenTerms.rateBps,
      flatAmountCents: event.flatAmountCents ?? frozenTerms.flatAmountCents,
      cookieWindowDays: frozenTerms.cookieWindowDays,
      payoutSchedule: frozenTerms.payoutSchedule,
      recurringMode: event.recurringMode ?? 'one_time',
      recurringMonths: event.recurringMonths ?? null,
      currency: frozenTerms.currency,
    },
  };
}

/** One line describing what an event pays, for the public terms table. */
export function describeEvent(event, fallbackTerms = null) {
  if (!event?.isPayable) return 'Tracked only';
  const { terms } = resolveEventTerms(
    { ...(fallbackTerms ?? {}), events: [event], primaryEventKey: event.key },
    event.key,
  );
  return terms ? describeTerms(terms) : 'Tracked only';
}

/** Events in funnel order, so a terms table reads the way a customer moves. */
export function sortEventsForDisplay(events = [], categoryOrder = {}) {
  return [...events].sort((a, b) => {
    const rank = (e) => categoryOrder[e.category] ?? 50;
    if (rank(a) !== rank(b)) return rank(a) - rank(b);
    // Payable actions above tracked-only ones at the same funnel depth.
    if (a.isPayable !== b.isPayable) return a.isPayable ? -1 : 1;
    return String(a.key).localeCompare(String(b.key));
  });
}

/**
 * What a card payment adds on top of an amount the brand owes: a percentage
 * plus a fixed amount, both from the server's config. Nothing on nothing.
 */
export function cardFeeCents(amountCents, { bps, fixedCents }) {
  if (!amountCents) return 0;
  return applyBps(amountCents, bps) + fixedCents;
}
